/**
 * PATH: src/pages/pricing/PricingPage.jsx
 * PURPOSE: Display role-based pricing plans
 */

import React, { useEffect, useState, useMemo } from "react";
import axios from "../../utils/axiosInstance";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom"; // ✅ ADD

import { Grid, Typography, CircularProgress, Box, Alert } from "@mui/material";
import PlanCard from "./components/PlanCard";

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate(); // ✅ ADD

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await axios.get("/api/billing/plans");
        setPlans(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("❌ Failed to fetch pricing plans", err);
        setError("Failed to load pricing plans");
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  /**
   * 🔐 ROLE-BASED PLAN FILTERING
   */
  const visiblePlans = useMemo(() => {
    if (!user) return [];

    switch (user.role) {
      case "mentor":
        return plans.filter(
          (p) => p.revenueStream === "mentor_subscription"
        );

      case "founder":
      case "user":
        return plans.filter(
          (p) =>
            p.revenueStream === "user_subscription" ||
            p.revenueStream === "training"
        );

      case "admin":
        return plans; // full visibility

      default:
        return [];
    }
  }, [plans, user]);

  if (loading) {
    return (
      <Box textAlign="center" mt={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box maxWidth={600} mx="auto" mt={8}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!visiblePlans.length) {
    return (
      <Box maxWidth={600} mx="auto" mt={8}>
        <Alert severity="info">
          No plans available for your account type.
        </Alert>
      </Box>
    );
  }

  /* =====================================================
     🚧 PRO LAUNCH GUARD (LITE-FIRST STRATEGY)
  ===================================================== */
  if (process.env.REACT_APP_PRO_ENABLED !== "true") {
    navigate("/lite/pro-coming-soon");
    return null;
  }

  return (
    <Box p={4}>
      <Typography variant="h4" fontWeight={700} mb={1}>
        Choose Your Plan
      </Typography>

      <Typography variant="body1" color="text.secondary" mb={4}>
        {user.role === "mentor"
          ? "Subscribe to activate your mentor profile and start earning."
          : "Upgrade to unlock advanced AI-powered startup tools."}
      </Typography>

      <Grid container spacing={3}>
        {visiblePlans.map((plan) => (
          <Grid item xs={12} sm={6} md={4} key={plan.code}>
            <PlanCard plan={plan} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
